import React from "react";
import { observer } from "mobx-react";
import { useQuery, useStore } from "../../stores/store";
import { useImperativeHandle, useState } from "react";
import { Button, FileInput, Grid, Group, Select, Text, TextInput } from "@mantine/core";
import t from "../../stores/language";
import { defaultModule } from "../module-creator";
import theme from "../../stores/theme";
import { Module, QueryKeys } from "../../models/models";
import { delay } from "./module-assumptions";
import { Icon } from "../../custom-components/icons";
import { faUpload } from "@fortawesome/pro-duotone-svg-icons";
import Modal from "../../custom-components/model";
import { useStyles } from "./configure-input";

export interface ExportImportHandle {
  show: (isImport: boolean) => void;
}
const ExportImportModule = observer(
  React.forwardRef<ExportImportHandle, unknown>(function ExportImportModule(props, ref) {
    const { store } = useStore();
    const { classes } = useStyles();
    const [module, setModule] = useState<Module>(defaultModule);
    const [isModuleImport, setModuleImport] = useState<boolean>(false);
    const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
    const [visible, setVisible] = useState<boolean>(false);
    const [selectedFile, setFile] = useState<File | null>(null);
    const [importing, setImporting] = useState<boolean>(false);

    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });

    const show = (isImport: boolean) => {
      if (isImport) {
        setModuleImport(true);
      } else {
        setModuleImport(false);
      }
      setVisible(true);
    };

    const { data: modules } = useQuery({
      queryKey: [QueryKeys.Modules],
      queryFn: async () => {
        return await store.GetAllModules();
      },
    });

    const resetState = () => {
      setVisible(false);
      if (isModuleImport && selectedFile != null) setFile(null);
      setModule({ id: 0, name: "", parentId: 0 });
      setError({ isError: false, message: "" });
    };

    const exportModule = async (module: Module) => {
      const result = await store.ExportModuleData(module.id);
      if (result) {
        const exportData = JSON.stringify(result);
        const moduleData = new Blob([exportData], { type: "application/json" });
        const fileURL = window.URL.createObjectURL(moduleData);
        const tempLink = document.createElement("a");
        tempLink.href = fileURL;
        tempLink.setAttribute("download", `${module.name}.json`);
        tempLink.click();
        setError({ isError: false, message: "Module Exported Successfully" });
        await delay(400);
      } else {
        setError({ isError: true, message: "Module Export Failed" });
        return;
      }
      resetState();
    };

    const importModule = async () => {
      if (module.name === "") {
        setError({ isError: true, message: t("Please Enter Module Name") });
        return;
      }
      if (selectedFile == null) {
        setError({ isError: true, message: t("Please Select File to Import") });
        return;
      }
      setImporting(true);
      const result = await store.ImportModuleData(module.name, module.parentId, selectedFile);
      if (result.error) {
        setError({ isError: true, message: result.message as string });
        setImporting(false);
        return;
      } else {
        setError({ isError: false, message: t("Imported Successfully") });
        await delay(400);
        setImporting(false);
      }
      resetState();
      setModuleImport(false);
    };

    return (
      <>
        <Modal
          opened={visible}
          header={isModuleImport ? t("Import Module") : t(`Export Module`)}
          footer={
            <Group className={classes.modalFooter}>
              <Button
                variant={"default"}
                onClick={() => {
                  resetState();
                }}
              >
                {t("Cancel")}
              </Button>
              <Button onClick={() => (isModuleImport ? importModule() : exportModule(module))} loading={importing}>
                {t("Ok")}
              </Button>
            </Group>
          }
          withCloseButton={true}
          onClose={() => {
            resetState();
          }}
          size={isModuleImport ? "50%" : "35%"}
        >
          <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
            {isModuleImport ? (
              <>
                <Grid.Col span={6}>
                  <TextInput
                    label={t("Name")}
                    radius={0}
                    value={module.name}
                    onChange={(e) => {
                      if (e.target.value !== "" && error.isError) {
                        setError({ isError: false, message: "" });
                      }
                      setModule({ ...module, name: e.target.value });
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Select
                    label={t("Parent")}
                    searchable
                    withinPortal
                    nothingFound={t("No module found")}
                    maxDropdownHeight={150}
                    data={(modules ?? []).map((d) => d.name)}
                    value={modules?.find((d) => d.id === module.parentId)?.name ?? null}
                    onChange={(v) => {
                      setModule({ ...module, parentId: modules?.find((d) => d.name === v)?.id ?? 0 });
                    }}
                  ></Select>
                </Grid.Col>
                <Grid.Col span={6}>
                  <FileInput
                    label={t("Import Module File")}
                    accept="application/json"
                    value={selectedFile}
                    onChange={(payload) => {
                      if (payload != null && error.isError) {
                        setError({ isError: false, message: "" });
                      }
                      setFile(payload);
                    }}
                    clearable
                    icon={<Icon icon={faUpload} />}
                  />
                </Grid.Col>
              </>
            ) : (
              <Grid.Col span={12}>
                <Select
                  label={t("Modules")}
                  searchable
                  withinPortal
                  nothingFound={t("No module found")}
                  maxDropdownHeight={150}
                  data={(modules ?? []).filter((m) => m.parentId != 0).map((d) => d.name)}
                  value={module.name}
                  onChange={(v) => {
                    setModule({ ...module, id: modules?.find((d) => d.name === v)?.id ?? 0, name: v ?? "" });
                  }}
                ></Select>
              </Grid.Col>
            )}
            {error.message !== "" ? (
              <Grid.Col span={12}>
                <Text size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"}>
                  {error.message}
                </Text>
              </Grid.Col>
            ) : null}
          </Grid>
        </Modal>
      </>
    );
  })
);

export default ExportImportModule;
